import { useState, useEffect, useMemo } from 'react';
import { analyticsService } from '../services/analyticsService';
import { resolveModelKey } from '../config/widgetRegistry';
import type { ColumnSchema, ResponseMeta } from '../types/analytics';

interface UseAnalyticsDataProps {
    templateKey?: string;
    metricKey?: string;
    queryConfig?: any;
    filters?: any[];
    /** Owner user ID for shared dashboard context - when viewing another user's dashboard */
    ownerUserId?: number | string;
    /** Widget scoping value (fixed per template variant) */
    scoping?: string;
    /** When false, skip fetching entirely (e.g. module not subscribed). Defaults to true. */
    enabled?: boolean;
}

interface UseAnalyticsDataResult {
    data: any;
    loading: boolean;
    error: string | null;
    /** @deprecated Use meta instead */
    metadata: any;
    /** Schema columns from V2 response */
    columns: ColumnSchema[];
    /** Response meta from V2 response */
    meta: ResponseMeta | null;
    /** Query time in milliseconds */
    queryTimeMs: number;
    /** Row count */
    rowCount: number;
}

export const useAnalyticsData = ({
    templateKey,
    metricKey,
    queryConfig,
    filters,
    ownerUserId,
    scoping,
    enabled = true,
}: UseAnalyticsDataProps): UseAnalyticsDataResult => {
    const [data, setData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [metadata, setMetadata] = useState<any>(null);
    const [columns, setColumns] = useState<ColumnSchema[]>([]);
    const [meta, setMeta] = useState<ResponseMeta | null>(null);

    // Serialize objects to stable strings for dependency comparison
    const queryConfigKey = useMemo(() => JSON.stringify(queryConfig), [queryConfig]);
    const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

    // Extract primitive values from queryConfig for dependency checks
    const dataSource = queryConfig?.data_source;
    const configMetricKey = queryConfig?.metric_key || queryConfig?.metricKey;
    const table = queryConfig?.table;

    // Determine if this should fetch data
    const shouldFetch = useMemo(() => {
        // Skip entirely if disabled (e.g. module not subscribed)
        if (!enabled) return false;

        // Exclude core backend widgets
        const isCoreWidget = templateKey === 'mp_user_card' ||
            metricKey?.startsWith('core_api:') ||
            dataSource === 'core_backend';

        if (isCoreWidget) return false;

        // Exclude review cloud widgets (they use useReviewCloudData hook)
        const isReviewCloudWidget = templateKey === 'mp_actions' ||
            metricKey?.startsWith('review_api:') ||
            dataSource === 'review_cloud';

        if (isReviewCloudWidget) return false;

        // Exclude mp_learning_plan only (direct learn-backend call via useLearnBackendData)
        if (templateKey === 'mp_learning_plan') return false;

        const hasMetricKey = metricKey || configMetricKey;
        const hasTable = table;

        return hasMetricKey || hasTable;
    }, [enabled, templateKey, metricKey, dataSource, configMetricKey, table]);

    useEffect(() => {
        if (!shouldFetch) return;

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                // Parse the serialized configs back
                const parsedQueryConfig = queryConfigKey ? JSON.parse(queryConfigKey) : {};
                const parsedFilters = filtersKey ? JSON.parse(filtersKey) : [];

                const resolvedMetricKey = metricKey || parsedQueryConfig?.metric_key || parsedQueryConfig?.metricKey;

                // Build merged config — pass scoping as scoping_type to the analytics API
                const mergedConfig = {
                    ...parsedQueryConfig,
                    metric_key: resolvedMetricKey,
                    filters: parsedFilters.length > 0 ? parsedFilters : parsedQueryConfig?.filters,
                    ...(scoping && { scoping_type: scoping }),
                };

                // Resolve model key (e.g., 'workforce' or 'reviewcloud')
                const modelKey = resolveModelKey({
                    templateKey,
                    metricKey: resolvedMetricKey
                });

                const response = await analyticsService.fetchWidgetDataFromConfig(mergedConfig, {
                    ownerUserId,
                    model: modelKey
                });

                setData(response.data);
                setMetadata(response.metadata);
                setColumns(response.schema?.columns ?? []);
                setMeta(response.meta ?? null);
            } catch (err: any) {
                console.error('Failed to fetch analytics data:', err);
                setError(err.message || 'Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [shouldFetch, templateKey, metricKey, queryConfigKey, filtersKey, ownerUserId, scoping]);

    return {
        data,
        loading,
        error,
        metadata,
        columns,
        meta,
        queryTimeMs: meta?.query_time_ms ?? 0,
        rowCount: meta?.row_count ?? 0,
    };
};
